import React, { useEffect } from 'react';
import { Box } from '@user-portal/wikr-components';
import { useTranslation } from 'react-i18next';

import { selectSubscriptions } from 'store/subscriptions/selectors';
import { setSubscriptionCancelReminder } from 'store/subscriptions/actions';
import { openModal } from 'store/modals/actions';

import {
  OfferTypes,
  cancelFlow_cancelOfferClick,
  cancelFlow_cancelOfferScreenLoad,
  cancelFlow_offerResultClick,
} from 'services/analytics/trackers/cancelFlow';

import { useDispatch, useSelector } from 'react-redux';
import { useCancelSubscription, useModals } from 'hooks';

import { reminderReasons } from 'containers/modals/ReminderReasonModalContainer/constants';
import ReminderReason from 'containers/modals/ReminderReasonModalContainer/components/ReminderReason/ReminderReason';
import ReasonCard from 'containers/modals/ReminderReasonModalContainer/components/ReasonCard';

import OutlinedButton from 'components/OutlinedButton';
import ModalHeader from 'components/ModalHeader';
import Modal from 'components/Modal';
import { Button, Text } from 'components/library';

import * as S from './styled';

import { ModalName } from 'types/modals';

const ReminderReasonModalContainer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { closeModal } = useModals();

  const { discardCancellation, onCancelSubscription } = useCancelSubscription();

  const { cancellationCandidate } = useSelector(selectSubscriptions);

  useEffect(() => {
    if (cancellationCandidate) {
      cancelFlow_cancelOfferScreenLoad({
        offer_type: OfferTypes.Reminder_offer,
        subscription_id: cancellationCandidate.external_id,
      });
    }
  }, [cancellationCandidate]);

  const setReminder = () => {
    dispatch(openModal(ModalName.WAITING_MODAL));

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'claim_offer',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Reminder_offer,
      });

      dispatch(
        setSubscriptionCancelReminder({
          externalId: cancellationCandidate.external_id,
          paymentProvider: cancellationCandidate.payment_provider,
          onError: () => {
            dispatch(
              openModal(ModalName.ERROR_MODAL, {
                tryAgain: () => {
                  cancelFlow_offerResultClick({
                    action: 'try_again',
                    result: 'fail',
                    offer_type: OfferTypes.Reminder_offer,
                    subscription_id: cancellationCandidate.external_id,
                  });
                  setReminder();
                },
              })
            );
          },
          onSuccess: () => {
            dispatch(openModal(ModalName.REMINDER_HAS_BEEN_SET_MODAL));
          },
          onCancel: () => null,
        })
      );
    }
  };

  const handleClose = () => {
    closeModal();
    discardCancellation();

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'close',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Reminder_offer,
      });
    }
  };

  const goBack = () => {
    dispatch(openModal(ModalName.REASON_MODAL));
  };

  const handleCancel = () => {
    onCancelSubscription();

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'cancel',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Reminder_offer,
      });
    }
  };

  return (
    <Modal isOpen fullscreen customStyles={{ content: { overflow: 'scroll' } }}>
      <ModalHeader onBack={goBack} onClose={handleClose} />
      <S.Container>
        <Box $paddingTop={20} $paddingBottom={100} $paddingX={16}>
          <Text type="h4" center mb={24}>
            {t('subscription.reminderModal.title')}{' '}
          </Text>
          <Text type="large-text" center mb={24}>
            {t('subscription.reminderModal.subTitle')}
          </Text>
          {reminderReasons.map((reason) => (
            <ReminderReason Badge={reason.badge} title={reason.title} key={reason.title}>
              {reason.children.map((reasonSubList) => {
                if (reasonSubList.type === 'card') {
                  return (
                    <ReasonCard
                      key={reasonSubList.title}
                      title={reasonSubList.title}
                      Badge={reasonSubList.badge}
                      bodyText={reasonSubList.bodyText}
                    />
                  );
                } else {
                  return (
                    <Text type="small-text" key={reasonSubList.bodyText}>
                      {t(reasonSubList.bodyText)}
                    </Text>
                  );
                }
              })}
            </ReminderReason>
          ))}
          <Button mb={20} onClick={setReminder} dataLocator="obContinue">
            {t('subscription.reminderModal.button.setReminder')}
          </Button>
          <OutlinedButton onClick={handleCancel} dataLocator="keepSubBtn">
            {t('subscription.text.cancel')}
          </OutlinedButton>
        </Box>
      </S.Container>
    </Modal>
  );
};

export default ReminderReasonModalContainer;
